import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Menu, X, ArrowRight } from "lucide-react";
import { LOGO, scrollToId } from "../lib/site";

const LINKS = [
  { label: "Services", id: "services" },
  { label: "Voice Demo", id: "voice-demo" },
  { label: "Process", id: "how-it-works" },
  { label: "Pricing", id: "pricing" },
  { label: "FAQ", id: "faq" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-[#07090E]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between gap-4">
        <button
          data-testid="nav-logo-button"
          onClick={() => window.__lenis?.scrollTo(0)}
          className="flex items-center gap-3 group"
        >
          <span className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 ring-1 ring-cyan-400/40 shadow-[0_0_24px_rgba(0,240,255,0.25)] overflow-hidden">
            <img src={LOGO} alt="TalkLogical logo" className="h-8 w-8 object-contain" />
          </span>
          <span className="font-display font-bold text-lg tracking-tight text-slate-50 group-hover:text-cyan-300 transition-colors">
            Talk<span className="text-cyan-400">Logical</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-slate-400 hover:text-cyan-300 transition-colors tracking-wide"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            data-testid="nav-status-pill"
            className="hidden md:flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3.5 py-1.5"
          >
            <Zap className="h-3.5 w-3.5 text-emerald-400" />
            <span className="text-xs font-mono font-semibold text-emerald-300 tracking-wide">
              AI VOICE NODES ONLINE
            </span>
          </div>
          <button
            data-testid="nav-cta-free-preview"
            onClick={() => go("preview")}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-bold text-[#07090E] hover:bg-cyan-300 hover:shadow-[0_0_28px_rgba(0,240,255,0.45)] transition-[background-color,box-shadow] duration-300"
          >
            Free Preview <ArrowRight className="h-4 w-4" />
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="lg:hidden flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 text-slate-300"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#07090E]/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left text-base font-medium text-slate-300 hover:text-cyan-300 transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-cta-free-preview"
                onClick={() => go("preview")}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 px-5 py-3 text-sm font-bold text-[#07090E]"
              >
                Free Preview <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
